import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { DatasetView } from '../types';

/** What the viewer last chose on one analysis's tables section. */
export interface AnalysisViewSelection {
  /** The table selector's option, or null until the viewer picks one. */
  selectedOptionId: string | null;
  /** The Analytical dataset's List/Table view, or null until the viewer picks one. */
  datasetView: DatasetView | null;
}

interface AnalysisViewState {
  selectionByProfileId: Record<string, AnalysisViewSelection>;
  /** Column visibility the viewer set on an analytical dataset's results grid. */
  columnVisibilityByDatasetId: Record<string, GridColumnVisibilityModel>;
}

interface AnalysisViewActions {
  /** Picking an option also drops the dataset view, so it opens on its default. */
  selectOption: (analysisProfileId: string, optionId: string) => void;
  selectDatasetView: (analysisProfileId: string, view: DatasetView) => void;
  setColumnVisibility: (datasetProfileId: string, model: GridColumnVisibilityModel) => void;
}

export const NO_VIEW_SELECTION: AnalysisViewSelection = {
  selectedOptionId: null,
  datasetView: null,
};

/**
 * The analysis page's view choices, kept outside the components so they survive the
 * page unmounting (the project page remounts its content pane when the panel layout
 * changes). In-memory only: a reload opens every analysis on its defaults.
 */
export const useAnalysisViewStore = create<AnalysisViewState & AnalysisViewActions>()(
  devtools(
    (set) => ({
      selectionByProfileId: {},
      columnVisibilityByDatasetId: {},
      selectOption: (analysisProfileId, optionId) =>
        set(
          (prev) => ({
            selectionByProfileId: {
              ...prev.selectionByProfileId,
              [analysisProfileId]: { selectedOptionId: optionId, datasetView: null },
            },
          }),
          false,
          'analysisView/selectOption'
        ),
      selectDatasetView: (analysisProfileId, view) =>
        set(
          (prev) => ({
            selectionByProfileId: {
              ...prev.selectionByProfileId,
              [analysisProfileId]: {
                ...(prev.selectionByProfileId[analysisProfileId] ?? NO_VIEW_SELECTION),
                datasetView: view,
              },
            },
          }),
          false,
          'analysisView/selectDatasetView'
        ),
      setColumnVisibility: (datasetProfileId, model) =>
        set(
          (prev) => ({
            columnVisibilityByDatasetId: {
              ...prev.columnVisibilityByDatasetId,
              [datasetProfileId]: model,
            },
          }),
          false,
          'analysisView/setColumnVisibility'
        ),
    }),
    { name: 'analysisView' }
  )
);

export const selectViewSelection =
  (analysisProfileId: string) =>
  (state: AnalysisViewState): AnalysisViewSelection =>
    state.selectionByProfileId[analysisProfileId] ?? NO_VIEW_SELECTION;

export const selectColumnVisibility =
  (datasetProfileId: string) =>
  (state: AnalysisViewState): GridColumnVisibilityModel | undefined =>
    state.columnVisibilityByDatasetId[datasetProfileId];
